import React, { Component } from 'react'
import { LayerGroup, FeatureGroup, Polygon } from 'react-leaflet'
import EditControl from '../tools/EditControl'
import { inject, observer } from 'mobx-react'
import TemporaryMarkerLayer from './TemporaryMarkerLayer'
import DraftPolygonLayer from './DraftPolygonLayer'
import DraftMarkerLayer from './DraftMarkerLayer'
import SavedMarkerLayer from './SavedMarkerLayer'
import Geopolygon from './Geopolygon'
import PointBufferLayer from './PointBufferLayer'
import ShapeLayer from './ShapeLayer'
import LineLayer from './LineLayer'
import MarkerLayer from './MarkerLayer'
import BoundaryLayer from './BoundaryLayer'
import UserShapefileLayer from './UserShapefileLayer'
import UserMarkerfileLayer from './UserMarkerfileLayer'
import IsochroneLayer from './IsochroneLayer'
import { selectionTypes } from 'constants.js'
import { getShapeArea } from '../../utils'

// Renders elements on the map, including polygons and markers
// in target groups, and interactive elements used for shape
// drawing and buffering.
@inject('dataStore', 'uiStore', 'selectionStore') @observer
class DrawLayer extends Component {
  // Convert GeoJSON coordinates [lng, lat] to Leaflet [lat, lng] vertex array for editing
  getPolygonPositions (geoJSON) {
    if (!geoJSON) return []
    const geometry = geoJSON.geometry || geoJSON
    if (!geometry || !geometry.coordinates || !geometry.coordinates.length) return []

    let ring = []
    if (geometry.type === 'Polygon') {
      ring = geometry.coordinates[0] || []
    } else if (geometry.type === 'MultiPolygon') {
      ring = (geometry.coordinates[0] && geometry.coordinates[0][0]) || []
    } else if (Array.isArray(geometry.coordinates)) {
      let curr = geometry.coordinates
      while (Array.isArray(curr) && Array.isArray(curr[0]) && Array.isArray(curr[0][0])) {
        curr = curr[0]
      }
      ring = curr
    }

    if (!Array.isArray(ring) || !ring.length) return []

    const latlngs = []
    ring.forEach(pt => {
      if (Array.isArray(pt) && pt.length >= 2 && pt[0] != null && pt[1] != null && !isNaN(pt[0]) && !isNaN(pt[1])) {
        latlngs.push([Number(pt[1]), Number(pt[0])])
      }
    })

    // Remove redundant closing point for Leaflet Draw vertex editor
    if (latlngs.length > 2) {
      const first = latlngs[0]
      const last = latlngs[latlngs.length - 1]
      if (Math.abs(first[0] - last[0]) < 1e-7 && Math.abs(first[1] - last[1]) < 1e-7) {
        latlngs.pop()
      }
    }

    return latlngs
  }

  // make [lat, long] coordinates appear as [long, lat]
  sortCoordinates (coordinates) {
    const sortedCoordinates = []
    coordinates.forEach((point, index) => {
      sortedCoordinates[index] = [point[1], point[0]]
    })
    return sortedCoordinates
  }

  render () {
    const { uiStore, selectionStore } = this.props
    let editLayer
    let createLayer

    if (selectionStore.editableShape && selectionStore.isEditing) {
      switch (selectionStore.type) {
        case selectionTypes.GEOPOLYGON: {
          const positions = this.getPolygonPositions(selectionStore.editableShape.geoJSON)
          if (positions.length >= 3) {
            editLayer = (
              <FeatureGroup key={`edit_geopolygon_${selectionStore.editableShape.id || selectionStore.editableShape.inner_id || 'shape'}`}>
                <Polygon
                  className='geopolygon editable white'
                  positions={positions}
                  pathOptions={{ color: 'red', fillColor: 'red', fillOpacity: 0.5 }}
                />
                <EditControl
                  position='topleft'
                  onMounted={(drawControl) => {
                    const handler = drawControl && drawControl._toolbars && drawControl._toolbars.edit && drawControl._toolbars.edit._modes && drawControl._toolbars.edit._modes.edit && drawControl._toolbars.edit._modes.edit.handler
                    if (handler) {
                      selectionStore.setEditControl(handler)
                      handler.enable()
                    }
                  }}
                  onEdited={e => {
                    if (!e.layers.getLayers().length) {
                      selectionStore.disableEdit()
                      selectionStore.select(
                        selectionStore.item,
                        { data: { group: selectionStore.data.group } },
                        false,
                        true
                      )
                    } else {
                      e.layers.eachLayer(a => {
                        // for shape-drag functionality
                        // a.dragging.enable()
                        uiStore.updateGeopolygon(selectionStore.data.group, selectionStore.editableShape, a.toGeoJSON())
                      })
                    }
                    // for shape-drag functionality
                    // selectionStore.disableMoving()
                  }}
                  edit={{ poly: true }}
                />
              </FeatureGroup>
            )
          } else {
            editLayer = null
          }
          break
        }
        case selectionTypes.POINT_BUFFER:
          editLayer = <PointBufferLayer pointBuffer={selectionStore.item} />
          break
        case selectionTypes.LINE:
          editLayer = <LineLayer line={selectionStore.item} />
          break
        default:
          editLayer = null
      }
    }

    if (!editLayer) {
      switch (selectionStore.type) {
        case selectionTypes.POINT_BUFFER:
          createLayer = <PointBufferLayer pointBuffer={selectionStore.item} />
          break
        case selectionTypes.SHAPE:
          createLayer = <ShapeLayer shape={selectionStore.item} />
          break
        case selectionTypes.LINE:
          createLayer = <LineLayer line={selectionStore.item} />
          break
        case selectionTypes.BOUNDARY_LIST:
          createLayer = <BoundaryLayer boundaryList={selectionStore.item} targetGroup={selectionStore.data.group} />
          break
        case selectionTypes.USER_SHAPEFILE_LIST:
          createLayer = <UserShapefileLayer userShapefileList={selectionStore.item} />
          break
        case selectionTypes.USER_MARKERFILE_LIST:
          createLayer = <UserMarkerfileLayer markerfile={selectionStore.item.selectedFile} />
          break
        case selectionTypes.MARKER:
          createLayer = (
            <MarkerLayer
              marker={selectionStore.item}
              draggable
              className='focused selected'
              onDragEnd={latLng => {
                selectionStore.item.position.latLng = latLng
              }}
            />
          )
          break
        case selectionTypes.ISOCHRONE:
          createLayer = <IsochroneLayer isochrone={selectionStore.item} />
          break
        default:
          createLayer = null
          break
      }
    }

    const visibleGroups = (uiStore.displayGroups || []).filter(group => group && group.visible)
    const allPolygonsWithGroup = []
    const allMarkersWithGroup = []

    visibleGroups.forEach(group => {
      const shapes = [
        ...(group.geopolygons || []),
        ...(group.zipCodeBoundaries || []),
        ...(group.boundaries || [])
      ]
      shapes.forEach(shape => {
        const isEditing = selectionStore.isEditing && selectionStore.editableShape && (
          selectionStore.editableShape === shape ||
          (shape.id != null && selectionStore.editableShape.id != null && String(shape.id) === String(selectionStore.editableShape.id)) ||
          (shape.inner_id != null && selectionStore.editableShape.inner_id != null && String(shape.inner_id) === String(selectionStore.editableShape.inner_id)) ||
          (shape.remote_db_id != null && selectionStore.editableShape.remote_db_id != null && String(shape.remote_db_id) === String(selectionStore.editableShape.remote_db_id))
        )
        if (!isEditing) {
          allPolygonsWithGroup.push({ shape, group })
        }
      })

      if (group.savedMarkers) {
        group.savedMarkers.forEach(marker => {
          allMarkersWithGroup.push({ marker, group })
        })
      }
    })

    allPolygonsWithGroup.sort((a, b) => getShapeArea(b.shape) - getShapeArea(a.shape))

    return (
      <LayerGroup>
        <LayerGroup>
          {allPolygonsWithGroup.map(({ shape, group }) => (
            <Geopolygon
              key={`${group.pk}_${shape.inner_id || shape.remote_db_id || shape.id}`}
              geopolygon={shape}
              group={group}
            />
          ))}
        </LayerGroup>
        <LayerGroup>
          {allMarkersWithGroup.map(({ marker, group }) => (
            <SavedMarkerLayer
              key={marker.id}
              marker={marker}
              group={group}
            />
          ))}
        </LayerGroup>
        <TemporaryMarkerLayer />
        <DraftPolygonLayer />
        <DraftMarkerLayer />
        {editLayer || createLayer}
      </LayerGroup>
    )
  }
}

export default DrawLayer
